import './GooglePay.css';

import {extractCSPNonce} from '../../shared/extractCspNonce';
import type {AppConfig} from '../initialize';
import {initialize} from '../initialize';
import injectScript from './injectScript';
import {focusFirstTabbable} from '../shared/domHelpers';
import type {RequestFn, SendFn} from '../setupMessages';

let isButtonLoaded: boolean;

const PAY_JS_URL = 'https://pay.google.com/gp/p/js/pay.js';

const handleStyleNonce = (nonce: string) => {
  const head = document.querySelector('head');
  const config = {childList: true, subtree: true};
  const addedList: Node[] = [];
  const callback = (mutationList: MutationRecord[]) => {
    mutationList.forEach((mutation) => {
      if (mutation.type === 'childList') {
        mutation.addedNodes.forEach((node) => {
          if (
            !addedList.includes(node) &&
            node instanceof HTMLElement &&
            node instanceof Node &&
            node.getAttribute('type') === 'text/css'
          ) {
            head?.removeChild(node);
            node.setAttribute('nonce', nonce);
            addedList.push(node);
            head?.appendChild(node);
          }
        });
      }
    });
  };
  const observer = new MutationObserver(callback);
  if (head) {
    observer.observe(head, config);
  }
  return observer;
};

const initializeButton = (
  send: SendFn<'GooglePay'>,
  request: RequestFn<'GooglePay'>,
  environment: google.payments.api.Environment,
  isReadyToPayEnvironment: google.payments.api.Environment,
  isReadyToPayRequest: google.payments.api.IsReadyToPayRequest,
  buttonOptions: Omit<google.payments.api.ButtonOptions, 'onClick'>,
  cachedIsReadyToPay?: boolean,
): void => {
  if (isButtonLoaded) {
    return;
  }

  let isPaymentAuthorizing = false;

  const onPaymentAuthorized = (
    data: google.payments.api.PaymentData,
  ): Promise<google.payments.api.PaymentAuthorizationResult> => {
    isPaymentAuthorizing = true;
    return new Promise((resolve) => {
      request({event: 'onPaymentAuthorized', data}).then((response) => {
        isPaymentAuthorizing = false;
        if (response.event === 'acceptPayment') {
          resolve({transactionState: 'SUCCESS'});
        } else if (response.event === 'rejectPayment') {
          resolve({
            transactionState: 'ERROR',
            error: response.error,
          });
        }
      });
    });
  };

  const onPaymentDataChanged = (
    intermediatePaymentData: google.payments.api.IntermediatePaymentData,
  ): Promise<google.payments.api.PaymentDataRequestUpdate> => {
    return new Promise((resolve) => {
      request({
        event: 'onPaymentDataChanged',
        intermediatePaymentData,
      }).then((response) => {
        if (response.event === 'acceptChange') {
          resolve(response.paymentDataRequestUpdate);
        } else if (response.event === 'rejectChange') {
          resolve({error: response.error});
        }
      });
    });
  };

  injectScript(
    PAY_JS_URL,
    () => {
      isButtonLoaded = true;
      const {google} = window;
      if (!google || !google.payments || !google.payments.api) {
        return;
      }

      // This client should be used if we are *not* collecting shipping or offer information
      const basicClient: google.payments.api.PaymentsClient =
        new google.payments.api.PaymentsClient({
          environment,
          paymentDataCallbacks: {onPaymentAuthorized},
        });
      // This client should be used if we are collecting shipping and/or offer information
      const paymentDataChangedClient: google.payments.api.PaymentsClient =
        new google.payments.api.PaymentsClient({
          environment,
          paymentDataCallbacks: {onPaymentAuthorized, onPaymentDataChanged},
        });
      // This client should be used only for the `isReadyToPay` call
      const isReadyToPayClient: google.payments.api.PaymentsClient =
        isReadyToPayEnvironment === environment
          ? basicClient
          : new google.payments.api.PaymentsClient({
              environment: isReadyToPayEnvironment,
              paymentDataCallbacks: {onPaymentAuthorized},
            });

      let isLoadInProgress = false;
      let button: HTMLElement;
      const showButton = () => {
        const buttonContainer = document.getElementById(
          'google-button-container',
        );
        if (!buttonContainer) return;

        // If a nonce is set for style-src on the CSP, we also need to set that for Google Pay
        // styles that are injected. So we listen for when the nodes are added, remove them,
        // then add them again with the included nonce. These are necessary for rendering the button.
        const nonce = extractCSPNonce('style');
        let observer: MutationObserver | null = null;
        if (nonce) {
          observer = handleStyleNonce(nonce);
        }

        button = basicClient.createButton({
          onClick: () => {
            request({event: 'onClick'}).then((response) => {
              observer?.disconnect();
              if (response.event === 'resolveClick') {
                const {callbackIntents} = response.paymentDataRequest;
                // We need to use the correct client if we're handling shipping or offer changes
                let localClient = basicClient;
                if (
                  callbackIntents?.indexOf('SHIPPING_ADDRESS') !== -1 ||
                  callbackIntents.indexOf('OFFER') !== -1
                ) {
                  localClient = paymentDataChangedClient;
                }

                if (!isLoadInProgress) {
                  isLoadInProgress = true;
                  localClient
                    .loadPaymentData(response.paymentDataRequest)
                    .then(() =>
                      // Success case - the payment sheet is closed
                      send({
                        event: 'onClose',
                      }),
                    )
                    .catch((rawError) => {
                      // Failure case - the payment sheet is closed
                      // Can be a Google Pay error or customer manually closes the sheet
                      // If onPaymentAuthorized is in-flight, suppress the cancel
                      // to avoid a race where cancel and confirm both fire
                      if (!isPaymentAuthorizing) {
                        send({
                          event: 'onClose',
                          rawError,
                        });
                      }
                    })
                    .finally(() => {
                      isLoadInProgress = false;
                    });
                }
              }
            });
          },
          ...buttonOptions,
        });

        buttonContainer.appendChild(button);
      };

      const sendIsReadyToPayResult = (
        response: google.payments.api.IsReadyToPayResponse,
      ) => {
        return request({event: 'isReadyToPay', payload: response}).then(
          (resp) => {
            if (resp.event === 'showButton' && resp.value) {
              showButton();
              // Waiting until after render to get the minWidth
              setTimeout(() => {
                // Classnames 'gpay-card-info-container' and 'gpay-button' are the two
                // possible button classnames, so we need to check both.
                const buyButton = button.querySelector(
                  '.gpay-card-info-container',
                );
                if (buyButton) {
                  const style = getComputedStyle(buyButton);
                  send({event: 'minWidth', minWidth: style.minWidth});
                  return;
                }
                const otherButton = button.querySelector('.gpay-button');
                if (otherButton) {
                  const style = getComputedStyle(otherButton);
                  send({event: 'minWidth', minWidth: style.minWidth});
                }
              }, 0);
            }
          },
        );
      };

      if (cachedIsReadyToPay !== undefined) {
        sendIsReadyToPayResult({result: cachedIsReadyToPay});
      } else {
        isReadyToPayClient
          .isReadyToPay(isReadyToPayRequest)
          .then((response) => sendIsReadyToPayResult(response))
          .catch((rawError) => {
            send({event: 'isReadyError', rawError});
          });
      }

      window.addEventListener('focus', () => {
        send({event: 'onFocus'});
        focusFirstTabbable('google-button-container');
      });

      window.addEventListener('blur', () => {
        send({event: 'onBlur'});
      });
    },
    () => {
      send({
        event: 'onError',
        error: 'Error loading Pay.js script',
      });
    },
  );
};

const app = (config: AppConfig<'GooglePay'>) => {
  config.subscribe((messageData) => {
    if (messageData.event === 'loadButton') {
      const {
        environment,
        isReadyToPayEnvironment,
        isReadyToPayRequest,
        buttonOptions,
        cachedIsReadyToPay,
      } = messageData;
      initializeButton(
        config.send,
        config.request,
        environment,
        isReadyToPayEnvironment,
        isReadyToPayRequest,
        buttonOptions,
        cachedIsReadyToPay,
      );
    }
  });
};

initialize({
  app,
  owner: 'elements-web',
});
